import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../lib/images";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

export default function BohoPicnic() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { type: "customization", ...form, details: { experience: "boho" } });
      toast.success(lang === "fr" ? "Votre demande a bien été envoyée !" : "Your request has been sent!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(lang === "fr" ? "Une erreur est survenue." : "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  const pkgs = [
    { key: "medium", name: t("boho.medium"), price: 59, includes: t("boho.mediumIncludes"), testid: "package-medium" },
    { key: "premium", name: t("boho.premium"), price: 79, includes: t("boho.premiumIncludes"), testid: "package-premium" },
  ];

  return (
    <div data-testid="boho-page">
      <SEO title="The Boho Picnic — Luxury Picnic Nice | The Nice Picnic"
        description="The Boho Picnic: a chic bohemian picnic in a secret spot near Nice. Rugs, cushions, Niçoise specialties, wine and decoration. From €59 per person." />

      <section className="relative min-h-[70svh] flex items-end pt-20">
        <div className="absolute inset-0">
          <img src={IMAGES.bohoHero} alt="The Boho Picnic — bohemian picnic spread on a rock terrace overlooking Nice and the Baie des Anges" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181513]/75 via-[#181513]/20 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 w-full">
          <Reveal>
            <p className="overline-label !text-gold">{t("boho.overline")}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ivory mt-4">The Boho Picnic</h1>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-7">
            <p className="text-lg sm:text-xl text-earth font-light leading-relaxed">{t("boho.intro")}</p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-earth">
              <span className="border-b border-gold pb-1">{t("boho.duration")}</span>
              <span className="border-b border-gold pb-1">{t("boho.times")} : 11:00 · 12:00 · 13:00 · 18:00 · 19:00</span>
            </div>
            <p className="mt-4 text-sm text-earth/70 font-light">{t("boho.timesNote")}</p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5 img-frame organic-crop">
            <img src={IMAGES.bohoDeco} alt="Picnic spread with picnic basket, tomatoes, red roses and The Nice Picnic chalkboard" className="w-full h-72 object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-cream" data-testid="boho-packages">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="overline-label">{t("boho.packages")}</p>
            <div className="w-16 h-px bg-gold mt-6 mx-auto" />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-14 max-w-5xl mx-auto">
            {pkgs.map((p, i) => (
              <Reveal key={p.key} delay={i * 0.12}>
                <div data-testid={p.testid} className={`h-full border ${p.key === "premium" ? "border-terracotta bg-ivory" : "border-champagne bg-ivory/60"} rounded-3xl p-8 sm:p-10`}>
                  {p.key === "premium" && (
                    <span className="text-[0.65rem] uppercase tracking-[0.25em] text-terracotta">Premium</span>
                  )}
                  <h3 className="font-serif text-3xl text-ink mt-2">{p.name}</h3>
                  <p className="mt-3"><span className="font-serif text-4xl text-clay">{p.price} €</span>
                    <span className="text-sm text-earth ml-2">{t("boho.perPerson")}</span></p>
                  <ul className="mt-7 space-y-3">
                    {p.includes.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-earth">
                        <Check size={15} className="text-olive mt-0.5 shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/booking?experience=boho&package=${p.key}`} data-testid={`book-${p.key}-btn`} className="btn-primary w-full mt-9">
                    {t("boho.bookCta")}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="boho-customization">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-6">
            <h2 className="font-serif text-4xl sm:text-5xl text-ink italic">{t("boho.makeItYours")}</h2>
            <p className="mt-6 text-earth font-light leading-relaxed">{t("boho.customP")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {t("boho.customItems").map((c) => (
                <span key={c} className="text-xs tracking-[0.12em] uppercase border border-champagne rounded-full px-4 py-2 text-earth">{c}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <form onSubmit={submit} className="border border-champagne rounded-3xl p-8 bg-cream/60" data-testid="boho-custom-form">
              <h3 className="font-serif text-2xl text-ink mb-6">{t("boho.formTitle")}</h3>
              <div className="space-y-4">
                <input data-testid="custom-name" required className="input-elegant" placeholder={t("booking.name")}
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="custom-email" required type="email" className="input-elegant" placeholder={t("booking.email")}
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="custom-phone" className="input-elegant" placeholder={t("booking.phone")}
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <textarea data-testid="custom-message" required rows={4} className="input-elegant" placeholder={t("booking.requestsPlaceholder")}
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button data-testid="custom-submit-btn" disabled={sending} className="btn-primary w-full disabled:opacity-60">
                  {t("boho.customCta")} <ArrowRight size={16} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
